// ─────────────────────────────────────────────────────────────────────────────
// Esquema declarativo de los componentes (zonas) que se pueden agregar a la
// página de inicio del ecommerce.
//
// Estructura:
//   COMPONENTES_CONFIG[tipo].label         → nombre del componente
//   COMPONENTES_CONFIG[tipo].variantes[v]  → variante específica
//     ├── label   → nombre visible de la variante
//     └── fields  → campos que pide al usuario
//
// Cada zona en el form se persiste como { tipo, variante, mostrar, titulo, ...fields }
// y los `fields` se resuelven contra COMPONENTES_CONFIG[tipo].variantes[variante].fields.
//
// Tipos de campo soportados (ver ZonaFieldsRenderer.js):
//   input | textarea | number | color | upload | select | switch | marcas |
//   group | list
// ─────────────────────────────────────────────────────────────────────────────

export const COMPONENTES_CONFIG = {
  notice: {
    label: "Notice (aviso desplazable)",
    variantes: {
      Simple: {
        label: "Notice Simple",
        descripcion:
          "Lista de textos que se desplazan horizontalmente en bucle. Ideal para anuncios cortos como envío gratis, cupones o avisos rotativos.",
        fields: [
          {
            type: "stringList",
            name: "textos",
            label: "Textos a mostrar",
            itemLabel: "Texto",
            defaultLength: 2,
            min: 2,
            max: 6,
            placeholder: "Ej: ENVÍO GRATIS EN COMPRAS MAYORES A $999",
            tooltip: "Cada texto se desplazará en bucle. Mínimo 2, máximo 6.",
          },
          {
            type: "group",
            columns: [
              {
                type: "select",
                name: "velocidad",
                label: "Velocidad",
                span: 8,
                options: [
                  { value: "lenta", label: "Lenta" },
                  { value: "normal", label: "Normal" },
                  { value: "rapida", label: "Rápida" },
                ],
              },
              {
                type: "color",
                name: "colorTexto",
                label: "Color del texto",
                span: 8,
              },
              {
                type: "color",
                name: "colorFondo",
                label: "Color de fondo",
                span: 8,
              },
            ],
          },
        ],
      },
    },
  },
  stories: {
    label: "Stories (tira tipo Instagram)",
    variantes: {
      Basico: {
        label: "Stories Básico",
        descripcion:
          "Tira horizontal de productos destacados con anillo degradado. Al hacer tap se abre un modal con la imagen y CTA al detalle del producto. Auto-avanza al siguiente.",
        fields: [
          {
            type: "number",
            name: "cantidad",
            label: "Cantidad de productos a mostrar",
            placeholder: "6",
            tooltip:
              "Recomendado entre 5 y 10. Si se omite, muestra todos los productos nuevos.",
          },
          {
            type: "select",
            name: "duracion",
            label: "Duración por slide (auto-avance)",
            options: [
              { value: "rapida", label: "Rápida (2s)" },
              { value: "normal", label: "Normal (3s)" },
              { value: "lenta", label: "Lenta (5s)" },
            ],
          },
        ],
      },
    },
  },

  marcasSlider: {
    label: "Marcas Slider",
    variantes: {
      BrandCarouselBasico: {
        label: "Carrusel de Marcas Básico",
        descripcion:
          "Muestra los logos de tus marcas en una fila con scroll. Al hacer clic redirige al catálogo filtrado por marca.",
        fields: [
          {
            type: "input",
            name: "titulo",
            label: "Título de la sección",
            placeholder: "Ej: Nuestras Marcas",
          },
          {
            type: "marcas",
            name: "seleccionadas",
            label: "Marcas a mostrar (solo disponibles si tienen logo)",
            tooltip: "Solo aparecen marcas que tengan logo registrado.",
          },
        ],
      },
    },
  },

  hero: {
    label: "Hero (banner principal)",
    variantes: {
      HeroBasico: {
        label: "Hero Básico (imagen fija de fondo + texto)",
        descripcion:
          "Banner con imagen de fondo fija y texto superpuesto. Ideal para destacar promociones o colecciones principales con un diseño visualmente impactante.",
        fields: [
          {
            type: "list",
            name: "slides",
            itemLabel: "Slide",
            defaultLength: 1,
            min: 1,
            max: 1,
            fields: [
              {
                type: "upload",
                name: "imagen",
                label: "Imagen de Fondo",
                tooltip:
                  "Imagen grande de alta calidad (1920x800px recomendado)",
              },
              {
                type: "group",
                columns: [
                  {
                    type: "input",
                    name: "textoSubtitulo",
                    label: "Subtítulo (Texto Pequeño)",
                    placeholder: "Ej: NUEVA COLECCIÓN",
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorSubtitulo",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
              {
                type: "group",
                columns: [
                  {
                    type: "input",
                    name: "textoTitulo",
                    label: "Título Principal",
                    placeholder: "Ej: Muévete rápido.",
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorTitulo",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
              {
                type: "group",
                columns: [
                  {
                    type: "textarea",
                    name: "textoDescripcion",
                    label: "Descripción",
                    placeholder: "Texto descriptivo...",
                    rows: 2,
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorDescripcion",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
            ],
          },
        ],
      },
      HeroSplit: {
        label: "Hero Split (imagen lateral + texto)",
        descripcion:
          "Layout dividido: imagen del lado izquierdo y bloque de texto (subtítulo, título, descripción) del lado derecho con color de fondo configurable. Incluye un botón fijo que lleva a la tienda. Diseño limpio y editorial.",
        fields: [
          {
            type: "list",
            name: "slides",
            itemLabel: "Slide",
            defaultLength: 1,
            min: 1,
            max: 1,
            fields: [
              {
                type: "upload",
                name: "imagen",
                label: "Imagen lateral",
                tooltip:
                  "Imagen vertical u horizontal de buena calidad. Ocupa la mitad izquierda en desktop y la parte superior en móvil (recomendado 800x800px).",
              },
              {
                type: "color",
                name: "colorFondo",
                label: "Color de fondo del texto",
                tooltip:
                  "Color del lado derecho donde van el título, subtítulo, descripción y botón. Se recomienda contraste con los colores del texto.",
              },
              {
                type: "group",
                columns: [
                  {
                    type: "input",
                    name: "textoSubtitulo",
                    label: "Subtítulo (Texto Pequeño)",
                    placeholder: "Ej: EDICIÓN LIMITADA",
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorSubtitulo",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
              {
                type: "group",
                columns: [
                  {
                    type: "input",
                    name: "textoTitulo",
                    label: "Título Principal",
                    placeholder: "Ej: Diseñado para durar",
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorTitulo",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
              {
                type: "group",
                columns: [
                  {
                    type: "textarea",
                    name: "textoDescripcion",
                    label: "Descripción",
                    placeholder: "Texto descriptivo...",
                    rows: 2,
                    span: 18,
                  },
                  {
                    type: "color",
                    name: "colorDescripcion",
                    label: "Color",
                    span: 6,
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  },

  carouselProductos: {
    label: "Carrusel de Productos",
    variantes: {
      CarouselBasico: {
        label: "Carrusel Básico ",
        descripcion:
          "Carrusel simple de productos en una fila con scroll horizontal. Compacto y rápido de explorar.",
        fields: [
          {
            type: "input",
            name: "titulo",
            label: "Título de la sección",
            placeholder: "Ej: Lo Más Nuevo",
          },
          {
            type: "number",
            name: "cantidad",
            label: "Cantidad de productos a mostrar",
            placeholder: "8",
          },
        ],
      },
      CarouselClasico: {
        label: "Carrusel Clásico (3 visibles)",
        descripcion:
          "Carrusel tradicional con 3 productos visibles a la vez. Mejor para destacar productos individualmente.",
        fields: [
          {
            type: "input",
            name: "titulo",
            label: "Título de la sección",
            placeholder: "Ej: Novedades",
          },
          {
            type: "number",
            name: "cantidad",
            label: "Cantidad de productos a mostrar",
            placeholder: "8",
          },
        ],
      },
      Carousel3D: {
        label: "Carrusel 3D",
        descripcion:
          "Carrusel con efecto 3D que da profundidad a la transición entre productos. Estética moderna y llamativa.",
        fields: [
          {
            type: "input",
            name: "titulo",
            label: "Título de la sección",
            placeholder: "Ej: Destacados",
          },
          {
            type: "number",
            name: "cantidad",
            label: "Cantidad de productos a mostrar",
            placeholder: "8",
          },
        ],
      },
      CarouselEsfera: {
        label: "Carrusel Esfera (3D interactivo)",
        descripcion:
          "Carrusel interactivo en forma de esfera 3D. Ideal para experiencias inmersivas y catálogos amplios.",
        fields: [
          {
            type: "input",
            name: "titulo",
            label: "Título de la sección",
            placeholder: "Ej: Explorar Productos",
          },
          {
            type: "number",
            name: "cantidad",
            label: "Cantidad de productos a mostrar",
            placeholder: "12",
          },
        ],
      },
    },
  },

  bannerCarrito: {
    label: "Banner de promoción",
    variantes: {
      BannerImagenUnica: {
        label: "Imagen alargada (una sola)",
        descripcion:
          "Una imagen full-width de extremo a extremo con un enlace de redirección.",
        fields: [
          {
            type: "upload",
            name: "imagen",
            label: "Imagen alargada",
            tooltip:
              "Imagen horizontal tipo franja que abarca todo el ancho. Tamaño recomendado: 1920 × 200 px (relación ≈ 9.6:1). El alto en la tienda se limita a 200px, así que usa una imagen baja y alargada. Formatos: JPG, PNG o WebP (idealmente < 250 KB).",
          },
          {
            type: "input",
            name: "url",
            label: "URL de redirección",
            placeholder: "https://...",
            tooltip:
              "Pega la URL del insumo o página de tienda a la que quieres llevar al cliente. Para obtenerla, entra al sitio, navega hasta el producto o la página que quieres y copia la URL de la barra del navegador.",
          },
        ],
      },
      BannerTresImagenes: {
        label: "Tres imágenes unidas",
        descripcion:
          "Tres imágenes pegadas en una sola franja full-width, cada una con su propio enlace de redirección.",
        fields: [
          {
            type: "list",
            name: "imagenes",
            itemLabel: "Imagen",
            defaultLength: 3,
            min: 3,
            max: 3,
            fields: [
              {
                type: "upload",
                name: "imagen",
                label: "Imagen",
                tooltip:
                  "Las tres se muestran unidas en una fila, así que usa las TRES del mismo tamaño para que queden parejas. Tamaño recomendado por imagen: 640 × 200 px (relación ≈ 3.2:1). El alto en la tienda se limita a 200px. Formatos: JPG, PNG o WebP (idealmente < 200 KB c/u).",
              },
              {
                type: "input",
                name: "url",
                label: "URL de redirección",
                placeholder: "https://...",
                tooltip:
                  "Pega la URL del insumo o página de tienda destino. Para obtenerla, entra al sitio, navega hasta el producto o la página que quieres y copia la URL de la barra del navegador.",
              },
            ],
          },
        ],
      },
    },
  },
};

// Opciones del SelectItem de tipo de zona
export const ZONA_OPTIONS = Object.entries(COMPONENTES_CONFIG).map(
  ([value, def]) => ({ value, label: def.label }),
);

// Opciones del SelectItem de variante para un tipo dado
export const getVarianteOptions = (tipo) => {
  const variantes = COMPONENTES_CONFIG[tipo]?.variantes || {};
  return Object.entries(variantes).map(([value, def]) => ({
    value,
    label: def.label,
  }));
};

// Descripción del variante seleccionado (texto explicativo)
export const getVarianteDescripcion = (tipo, variante) => {
  if (!tipo || !variante) return "";
  return COMPONENTES_CONFIG[tipo]?.variantes?.[variante]?.descripcion || "";
};

// Título a mostrar en el header de la zona según los valores actuales
export const getZonaTitulo = (index, tipo, variante) => {
  if (tipo && variante) {
    const v = COMPONENTES_CONFIG[tipo]?.variantes?.[variante];
    if (v?.label) return v.label;
  }
  if (tipo) {
    const t = COMPONENTES_CONFIG[tipo];
    if (t?.label) return t.label;
  }
  return `Zona ${index + 1}`;
};

// ─── Procesamiento de uploads dentro de las zonas ────────────────────────────

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });

const procesarUpload = async (value) => {
  if (Array.isArray(value) && value.length > 0 && value[0]?.originFileObj) {
    const file = value[0].originFileObj;
    const base64 = await fileToBase64(file);
    return [{ name: file.name, url: base64 }];
  }
  return value;
};

const procesarCampo = async (fieldDef, container) => {
  if (!container) return;
  if (fieldDef.type === "upload") {
    if (container[fieldDef.name] !== undefined) {
      container[fieldDef.name] = await procesarUpload(container[fieldDef.name]);
    }
  } else if (fieldDef.type === "group") {
    for (const col of fieldDef.columns || []) {
      await procesarCampo(col, container);
    }
  } else if (fieldDef.type === "list") {
    const items = container[fieldDef.name];
    if (Array.isArray(items)) {
      for (const item of items) {
        for (const sub of fieldDef.fields || []) {
          await procesarCampo(sub, item);
        }
      }
    }
  }
};

export const procesarZonas = async (zonas) => {
  if (!Array.isArray(zonas)) return [];
  const out = [];
  for (const zona of zonas) {
    if (!zona?.tipo || !zona?.variante) {
      out.push(zona);
      continue;
    }
    const config = COMPONENTES_CONFIG[zona.tipo]?.variantes?.[zona.variante];
    if (!config) {
      out.push(zona);
      continue;
    }
    const copia = { ...zona, datos: { ...(zona.datos || {}) } };
    for (const fieldDef of config.fields || []) {
      await procesarCampo(fieldDef, copia.datos);
    }
    out.push(copia);
  }
  return out;
};

// ─── Configuración del Layout (header / footer) ──────────────────────────────
// El subHeader reutiliza COMPONENTES_CONFIG (filtrado por SUB_HEADER_TIPOS_DISPONIBLES).

export const LAYOUT_CONFIG = {
  header: {
    label: "Header",
    variantes: {
      HeaderBase: {
        label: "Header Base",
        descripcion: "Header con logo, menú de categorías, buscador y carrito.",
        fields: [],
      },
    },
  },
  footer: {
    label: "Footer",
    variantes: {
      FooterBase: {
        label: "Footer Base",
        descripcion:
          "Footer con enlaces, redes sociales e información de contacto.",
        fields: [],
      },
    },
  },
};

// Tipos de componente disponibles para el subHeader.
// De momento solo el notice, pero en el futuro se podrían sumar más.
export const SUB_HEADER_TIPOS_DISPONIBLES = ["notice", "stories"];

export const getSubHeaderTipoOptions = () =>
  SUB_HEADER_TIPOS_DISPONIBLES.map((tipo) => ({
    value: tipo,
    label: COMPONENTES_CONFIG[tipo]?.label || tipo,
  }));

// Helpers para layout (header / footer)
export const getLayoutVarianteOptions = (slot) => {
  const variantes = LAYOUT_CONFIG[slot]?.variantes || {};
  return Object.entries(variantes).map(([value, def]) => ({
    value,
    label: def.label,
  }));
};

export const getLayoutVarianteDescripcion = (slot, variante) => {
  if (!slot || !variante) return "";
  return LAYOUT_CONFIG[slot]?.variantes?.[variante]?.descripcion || "";
};

export const getLayoutVarianteFields = (slot, variante) => {
  return LAYOUT_CONFIG[slot]?.variantes?.[variante]?.fields || [];
};

// Procesa los uploads dentro de layout.header / layout.subHeader / layout.footer
export const procesarLayout = async (layout) => {
  if (!layout) return layout;
  const out = { ...layout };

  // Header
  if (out.header?.variante) {
    const cfg = LAYOUT_CONFIG.header?.variantes?.[out.header.variante];
    if (cfg) {
      const datos = { ...(out.header.datos || {}) };
      for (const fieldDef of cfg.fields || []) {
        await procesarCampo(fieldDef, datos);
      }
      out.header = { ...out.header, datos };
    }
  }

  // Footer
  if (out.footer?.variante) {
    const cfg = LAYOUT_CONFIG.footer?.variantes?.[out.footer.variante];
    if (cfg) {
      const datos = { ...(out.footer.datos || {}) };
      for (const fieldDef of cfg.fields || []) {
        await procesarCampo(fieldDef, datos);
      }
      out.footer = { ...out.footer, datos };
    }
  }

  // SubHeader (usa COMPONENTES_CONFIG)
  if (out.subHeader?.activo && out.subHeader?.tipo && out.subHeader?.variante) {
    const cfg =
      COMPONENTES_CONFIG[out.subHeader.tipo]?.variantes?.[
        out.subHeader.variante
      ];
    if (cfg) {
      const datos = { ...(out.subHeader.datos || {}) };
      for (const fieldDef of cfg.fields || []) {
        await procesarCampo(fieldDef, datos);
      }
      out.subHeader = { ...out.subHeader, datos };
    }
  }

  if (out.bannerCarrito) {
    const slots = { ...out.bannerCarrito };
    for (const slotKey of ["superior", "inferior"]) {
      const slot = slots[slotKey];
      if (slot?.variante) {
        const cfg =
          COMPONENTES_CONFIG.bannerCarrito?.variantes?.[slot.variante];
        if (cfg) {
          const datos = { ...(slot.datos || {}) };
          for (const fieldDef of cfg.fields || []) {
            await procesarCampo(fieldDef, datos);
          }
          slots[slotKey] = { ...slot, datos };
        }
      }
    }
    out.bannerCarrito = slots;
  }

  return out;
};
